<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useVoiceNotesWidget } from '@/composables/useVoiceNotesWidget'
import { useVoiceNotesStore } from '@/views/VoiceNotes/store'
import { useToast } from '@/components/ui/toast'
import type { VoiceSession, VoiceFragment } from '@/views/VoiceNotes/types'

import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Separator } from '@/components/ui/separator'
import {
  Mic,
  Clock,
  ChevronLeft,
  Trash2,
  Pencil,
  Check,
  X,
  Minus,
  Loader2,
  MessageSquareText,
  Hash,
  RefreshCw,
  Search,
  BrainCircuit,
  Sparkles,
  Send,
  RotateCcw,
  CircleHelp,
  Users,
  ListChecks,
  Tags,
} from 'lucide-vue-next'

const { isOpen, minimize, close } = useVoiceNotesWidget()
const store = useVoiceNotesStore()
const { toast } = useToast()

// View state
const view = ref<'list' | 'detail'>('list')
const showDeleteSession = ref(false)
const showDeleteFragment = ref(false)
const sessionToDelete = ref<VoiceSession | null>(null)
const fragmentToDelete = ref<VoiceFragment | null>(null)
const editingFragmentId = ref<number | null>(null)
const editingFragmentText = ref('')
const editingSessionId = ref<number | null>(null)
const editingSessionName = ref('')
const searchQuery = ref('')
const showAnalysis = ref(false)
const customPrompt = ref('')
const showDeleteAnalysis = ref(false)

const filteredSessions = computed(() => {
  const q = searchQuery.value.trim().toLowerCase()
  if (!q) return store.sessions
  return store.sessions.filter((s) =>
    s.name.toLowerCase().includes(q) ||
    (s.description && s.description.toLowerCase().includes(q)) ||
    (s.tags && s.tags.toLowerCase().includes(q))
  )
})

// Load all sessions when panel opens
watch(isOpen, (open) => {
  if (open) {
    view.value = 'list'
    searchQuery.value = ''
    store.clearFragments()
    store.fetchAllSessions()
  }
})

const formatDuration = (seconds: number | null): string => {
  if (!seconds) return '0s'
  const mins = Math.floor(seconds / 60)
  const secs = Math.round(seconds % 60)
  if (mins === 0) return `${secs}s`
  return `${mins}m ${secs}s`
}

const formatDate = (dateStr: string): string => {
  const d = new Date(dateStr)
  return d.toLocaleDateString('es-CL', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const statusLabels: Record<string, string> = {
  active: 'Activa',
  completed: 'Completada',
  archived: 'Archivada',
}

const statusColors: Record<string, string> = {
  active: 'bg-green-500/10 text-green-600',
  completed: 'bg-blue-500/10 text-blue-600',
  archived: 'bg-gray-500/10 text-gray-600',
}

const entityLabel = (session: VoiceSession): string | null => {
  if (session.projectId) return 'Proyecto'
  if (session.applicationId) return 'Aplicacion'
  if (session.meetingId) return 'Reunion'
  return null
}

// Session actions
const openSession = (session: VoiceSession) => {
  store.fetchFragments(session.id)
  store.fetchAnalysis(session.id)
  showAnalysis.value = false
  view.value = 'detail'
}

const goBack = () => {
  view.value = 'list'
  showAnalysis.value = false
  customPrompt.value = ''
  store.clearFragments()
}

// Analysis actions
const runAnalysis = async () => {
  if (!store.currentSession) return
  try {
    await store.analyzeSession(store.currentSession.id)
    showAnalysis.value = true
    toast({ title: 'Análisis generado' })
  } catch {
    toast({ title: 'Error al analizar la sesión', variant: 'destructive' })
  }
}

const runCustomPrompt = async () => {
  if (!store.currentSession || !customPrompt.value.trim()) return
  try {
    await store.analyzeCustom(store.currentSession.id, customPrompt.value.trim())
    customPrompt.value = ''
    toast({ title: 'Análisis personalizado generado' })
  } catch {
    toast({ title: 'Error al ejecutar prompt personalizado', variant: 'destructive' })
  }
}

const confirmDeleteAnalysis = () => {
  showDeleteAnalysis.value = true
}

const doDeleteAnalysis = async () => {
  if (!store.currentSession) return
  try {
    await store.deleteAnalysis(store.currentSession.id)
    showAnalysis.value = false
    toast({ title: 'Análisis eliminado' })
  } catch {
    toast({ title: 'Error al eliminar análisis', variant: 'destructive' })
  } finally {
    showDeleteAnalysis.value = false
  }
}

const startEditSession = (session: VoiceSession) => {
  editingSessionId.value = session.id
  editingSessionName.value = session.name
}

const saveSessionName = async () => {
  if (!editingSessionId.value || !editingSessionName.value.trim()) return
  try {
    await store.updateSession(editingSessionId.value, { name: editingSessionName.value.trim() })
    toast({ title: 'Sesion actualizada' })
  } catch {
    toast({ title: 'Error al actualizar', variant: 'destructive' })
  } finally {
    editingSessionId.value = null
  }
}

const confirmDeleteSession = (session: VoiceSession) => {
  sessionToDelete.value = session
  showDeleteSession.value = true
}

const doDeleteSession = async () => {
  if (!sessionToDelete.value) return
  try {
    await store.deleteSession(sessionToDelete.value.id)
    toast({ title: 'Sesion eliminada' })
  } catch {
    toast({ title: 'Error al eliminar', variant: 'destructive' })
  } finally {
    showDeleteSession.value = false
    sessionToDelete.value = null
  }
}

// Fragment actions
const startEditFragment = (fragment: VoiceFragment) => {
  editingFragmentId.value = fragment.id
  editingFragmentText.value = fragment.text
}

const saveFragmentText = async () => {
  if (!editingFragmentId.value || !editingFragmentText.value.trim()) return
  try {
    await store.updateFragment(editingFragmentId.value, editingFragmentText.value.trim())
    toast({ title: 'Fragmento actualizado' })
  } catch {
    toast({ title: 'Error al actualizar', variant: 'destructive' })
  } finally {
    editingFragmentId.value = null
  }
}

const confirmDeleteFragment = (fragment: VoiceFragment) => {
  fragmentToDelete.value = fragment
  showDeleteFragment.value = true
}

const doDeleteFragment = async () => {
  if (!fragmentToDelete.value) return
  try {
    await store.deleteFragment(fragmentToDelete.value.id)
    toast({ title: 'Fragmento eliminado' })
  } catch {
    toast({ title: 'Error al eliminar', variant: 'destructive' })
  } finally {
    showDeleteFragment.value = false
    fragmentToDelete.value = null
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-all duration-300 ease-out"
      leave-active-class="transition-all duration-200 ease-in"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div v-if="isOpen" class="fixed inset-0 z-[100] bg-background flex flex-col">
        <!-- Header -->
        <div class="h-14 border-b px-6 py-3 flex items-center justify-between shrink-0">
          <div class="flex items-center gap-2">
            <template v-if="view === 'detail' && store.currentSession">
              <Button variant="ghost" size="icon" class="h-8 w-8" @click="goBack">
                <ChevronLeft class="h-4 w-4" />
              </Button>
              <Mic class="h-5 w-5" />
              <h1 class="text-lg font-semibold truncate">{{ store.currentSession.name }}</h1>
            </template>
            <template v-else>
              <Mic class="h-5 w-5" />
              <h1 class="text-lg font-semibold">Notas de Voz</h1>
              <Badge variant="outline">{{ store.sessions.length }}</Badge>
            </template>
          </div>
          <div class="flex items-center gap-2">
            <Button
              v-if="view === 'list'"
              variant="outline"
              size="sm"
              @click="store.fetchAllSessions()"
              :disabled="store.isLoading"
            >
              <Loader2 v-if="store.isLoading" class="h-3.5 w-3.5 mr-1.5 animate-spin" />
              <RefreshCw v-else class="h-3.5 w-3.5 mr-1.5" />
              Actualizar
            </Button>
            <Button variant="ghost" size="icon" title="Minimizar" @click="minimize">
              <Minus class="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" title="Cerrar" @click="close">
              <X class="h-4 w-4" />
            </Button>
          </div>
        </div>

        <!-- Content -->
        <div class="flex-1 min-h-0 overflow-auto">
          <!-- Loading -->
          <div v-if="store.isLoading && view === 'list'" class="flex items-center justify-center py-12">
            <Loader2 class="h-6 w-6 animate-spin text-muted-foreground" />
          </div>

          <!-- Sessions List -->
          <div v-else-if="view === 'list'" class="max-w-3xl mx-auto p-6">
            <!-- Search -->
            <div v-if="store.sessions.length > 0" class="mb-4">
              <div class="relative">
                <Search class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  v-model="searchQuery"
                  placeholder="Buscar por titulo, descripcion o tags..."
                  class="pl-9"
                />
              </div>
            </div>

            <div v-if="store.sessions.length === 0" class="text-center py-16">
              <Mic class="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p class="text-muted-foreground">No hay notas de voz</p>
            </div>

            <div v-else-if="filteredSessions.length === 0" class="text-center py-12">
              <Search class="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p class="text-sm text-muted-foreground">Sin resultados para "{{ searchQuery }}"</p>
            </div>

            <div v-else class="space-y-2">
              <div
                v-for="session in filteredSessions"
                :key="session.id"
                class="flex items-center gap-3 p-4 border rounded-lg hover:bg-muted/50 transition-colors group"
              >
                <div
                  class="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0 cursor-pointer"
                  @click="openSession(session)"
                >
                  <Mic class="h-5 w-5 text-primary" />
                </div>

                <div class="flex-1 min-w-0 cursor-pointer" @click="openSession(session)">
                  <!-- Editing name -->
                  <div v-if="editingSessionId === session.id" class="flex items-center gap-2" @click.stop>
                    <Input
                      v-model="editingSessionName"
                      class="h-7 text-sm"
                      @keydown.enter="saveSessionName"
                      @keydown.escape="editingSessionId = null"
                    />
                    <Button variant="ghost" size="icon" class="h-7 w-7 shrink-0" @click="saveSessionName">
                      <Check class="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" class="h-7 w-7 shrink-0" @click="editingSessionId = null">
                      <X class="h-3.5 w-3.5" />
                    </Button>
                  </div>

                  <template v-else>
                    <p class="text-sm font-medium truncate">{{ session.name }}</p>
                    <div class="flex items-center gap-2 text-xs text-muted-foreground mt-0.5 flex-wrap">
                      <Badge variant="secondary" :class="statusColors[session.status]" class="text-[10px] px-1.5 py-0">
                        {{ statusLabels[session.status] || session.status }}
                      </Badge>
                      <Badge v-if="entityLabel(session)" variant="outline" class="text-[10px] px-1.5 py-0">
                        {{ entityLabel(session) }}
                      </Badge>
                      <span class="flex items-center gap-1">
                        <MessageSquareText class="h-3 w-3" />
                        {{ session.fragmentCount }}
                      </span>
                      <span v-if="session.totalDuration" class="flex items-center gap-1">
                        <Clock class="h-3 w-3" />
                        {{ formatDuration(session.totalDuration) }}
                      </span>
                      <span>{{ formatDate(session.createdAt) }}</span>
                    </div>
                    <p v-if="session.description" class="text-xs text-muted-foreground/70 mt-0.5 truncate">
                      {{ session.description }}
                    </p>
                  </template>
                </div>

                <div class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    class="h-7 w-7"
                    @click.stop="startEditSession(session)"
                    title="Editar nombre"
                  >
                    <Pencil class="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    class="h-7 w-7 text-muted-foreground hover:text-destructive"
                    @click.stop="confirmDeleteSession(session)"
                    title="Eliminar sesion"
                  >
                    <Trash2 class="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <!-- Session Detail (Fragments) -->
          <div v-else-if="view === 'detail'" class="max-w-3xl mx-auto p-6">
            <!-- Session info -->
            <div class="mb-4 flex items-center gap-2 text-xs text-muted-foreground">
              <span v-if="store.currentSession?.totalDuration" class="flex items-center gap-1">
                <Clock class="h-3 w-3" />
                {{ formatDuration(store.currentSession.totalDuration) }}
              </span>
              <span>{{ store.fragmentCount }} fragmentos</span>
              <span v-if="store.currentSession?.tags" class="text-muted-foreground/70">
                {{ store.currentSession.tags }}
              </span>
            </div>

            <p v-if="store.currentSession?.description" class="text-sm text-muted-foreground mb-4">
              {{ store.currentSession.description }}
            </p>

            <!-- Analysis Section -->
            <div class="mb-4">
              <div class="flex items-center gap-2 mb-2">
                <Button
                  v-if="store.analysis"
                  variant="outline"
                  size="sm"
                  @click="showAnalysis = !showAnalysis"
                >
                  <BrainCircuit class="h-4 w-4 mr-1" />
                  {{ showAnalysis ? 'Ocultar análisis' : 'Ver análisis' }}
                </Button>
                <Button
                  v-if="!store.analysis && !store.isLoadingAnalysis"
                  variant="outline"
                  size="sm"
                  :disabled="store.isAnalyzing || store.fragments.length === 0"
                  @click="runAnalysis"
                >
                  <Sparkles class="h-4 w-4 mr-1" />
                  <Loader2 v-if="store.isAnalyzing" class="h-4 w-4 animate-spin mr-1" />
                  Generar análisis IA
                </Button>
                <Loader2 v-if="store.isLoadingAnalysis" class="h-4 w-4 animate-spin text-muted-foreground" />
              </div>

              <div v-if="showAnalysis && store.analysis" class="border rounded-lg p-4 space-y-4 bg-muted/30">
                <div v-if="store.analysis.summary">
                  <h4 class="text-sm font-semibold mb-1 flex items-center gap-1.5">
                    <BrainCircuit class="h-4 w-4 text-primary" />
                    Resumen
                  </h4>
                  <p class="text-sm leading-relaxed">{{ store.analysis.summary }}</p>
                </div>

                <div v-if="store.analysis.topics.length > 0">
                  <h4 class="text-sm font-semibold mb-1 flex items-center gap-1.5">
                    <Tags class="h-4 w-4 text-primary" />
                    Temas
                  </h4>
                  <div class="flex flex-wrap gap-1.5">
                    <Badge v-for="topic in store.analysis.topics" :key="topic" variant="secondary" class="text-xs">
                      {{ topic }}
                    </Badge>
                  </div>
                </div>

                <div v-if="store.analysis.decisions.length > 0">
                  <h4 class="text-sm font-semibold mb-1 flex items-center gap-1.5">
                    <ListChecks class="h-4 w-4 text-primary" />
                    Decisiones
                  </h4>
                  <ul class="text-sm space-y-1 list-disc list-inside">
                    <li v-for="(decision, i) in store.analysis.decisions" :key="i">{{ decision }}</li>
                  </ul>
                </div>

                <div v-if="store.analysis.keyQuestions.length > 0">
                  <h4 class="text-sm font-semibold mb-1 flex items-center gap-1.5">
                    <CircleHelp class="h-4 w-4 text-primary" />
                    Preguntas clave
                  </h4>
                  <ul class="text-sm space-y-1 list-disc list-inside">
                    <li v-for="(q, i) in store.analysis.keyQuestions" :key="i">{{ q }}</li>
                  </ul>
                </div>

                <div v-if="store.analysis.speakers.length > 0">
                  <h4 class="text-sm font-semibold mb-1 flex items-center gap-1.5">
                    <Users class="h-4 w-4 text-primary" />
                    Participantes
                  </h4>
                  <div class="flex flex-wrap gap-1.5">
                    <Badge v-for="speaker in store.analysis.speakers" :key="speaker" variant="outline" class="text-xs">
                      {{ speaker }}
                    </Badge>
                  </div>
                </div>

                <div v-if="store.analysis.customResult">
                  <Separator class="my-2" />
                  <h4 class="text-sm font-semibold mb-1 flex items-center gap-1.5">
                    <Sparkles class="h-4 w-4 text-primary" />
                    Resultado personalizado
                  </h4>
                  <p class="text-xs text-muted-foreground mb-1 italic">{{ store.analysis.customPrompt }}</p>
                  <div class="text-sm whitespace-pre-wrap leading-relaxed bg-background rounded p-3 border">{{ store.analysis.customResult }}</div>
                </div>

                <Separator class="my-2" />
                <div class="flex items-center gap-2">
                  <Input
                    v-model="customPrompt"
                    placeholder="Prompt personalizado (ej: genera un acta formal...)"
                    class="text-sm"
                    @keydown.enter="runCustomPrompt"
                  />
                  <Button
                    variant="default"
                    size="icon"
                    class="h-9 w-9 shrink-0"
                    :disabled="store.isAnalyzing || !customPrompt.trim()"
                    @click="runCustomPrompt"
                  >
                    <Loader2 v-if="store.isAnalyzing" class="h-4 w-4 animate-spin" />
                    <Send v-else class="h-4 w-4" />
                  </Button>
                </div>

                <div class="flex items-center gap-2 pt-1">
                  <Button variant="ghost" size="sm" :disabled="store.isAnalyzing" @click="runAnalysis">
                    <RotateCcw class="h-3.5 w-3.5 mr-1" />
                    Re-analizar
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    class="text-muted-foreground hover:text-destructive"
                    @click="confirmDeleteAnalysis"
                  >
                    <Trash2 class="h-3.5 w-3.5 mr-1" />
                    Eliminar análisis
                  </Button>
                  <span class="text-xs text-muted-foreground ml-auto">
                    {{ store.analysis.modelUsed }}
                  </span>
                </div>
              </div>

              <div v-if="store.isAnalyzing && !store.analysis" class="border rounded-lg p-6 flex flex-col items-center justify-center gap-2 bg-muted/30">
                <Loader2 class="h-6 w-6 animate-spin text-primary" />
                <p class="text-sm text-muted-foreground">Analizando sesión con IA...</p>
              </div>
            </div>

            <!-- Loading fragments -->
            <div v-if="store.isLoadingFragments" class="flex items-center justify-center py-8">
              <Loader2 class="h-5 w-5 animate-spin text-muted-foreground" />
            </div>

            <!-- Empty -->
            <div v-else-if="store.fragments.length === 0" class="text-center py-8 border rounded-lg">
              <MessageSquareText class="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p class="text-sm text-muted-foreground">Sin fragmentos de audio</p>
            </div>

            <!-- Fragment list -->
            <div v-else class="space-y-2">
              <div
                v-for="(fragment, index) in store.fragments"
                :key="fragment.id"
                class="p-3 border rounded-lg group"
              >
                <div class="flex items-start gap-2">
                  <div class="flex items-center gap-1.5 text-xs text-muted-foreground shrink-0 mt-0.5 w-16">
                    <Hash class="h-3 w-3" />
                    <span>{{ index + 1 }}</span>
                    <span class="text-muted-foreground/50">{{ formatDuration(fragment.duration) }}</span>
                  </div>

                  <div class="flex-1 min-w-0">
                    <!-- Editing -->
                    <div v-if="editingFragmentId === fragment.id">
                      <Textarea
                        v-model="editingFragmentText"
                        class="text-sm min-h-[60px]"
                        @keydown.escape="editingFragmentId = null"
                      />
                      <div class="flex items-center gap-2 mt-2">
                        <Button size="sm" variant="default" @click="saveFragmentText">
                          <Check class="h-3.5 w-3.5 mr-1" />
                          Guardar
                        </Button>
                        <Button size="sm" variant="ghost" @click="editingFragmentId = null">
                          Cancelar
                        </Button>
                      </div>
                    </div>
                    <!-- Display -->
                    <p v-else class="text-sm leading-relaxed">{{ fragment.text }}</p>
                  </div>

                  <div
                    v-if="editingFragmentId !== fragment.id"
                    class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                  >
                    <Button
                      variant="ghost"
                      size="icon"
                      class="h-7 w-7"
                      @click="startEditFragment(fragment)"
                      title="Editar texto"
                    >
                      <Pencil class="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      class="h-7 w-7 text-muted-foreground hover:text-destructive"
                      @click="confirmDeleteFragment(fragment)"
                      title="Eliminar fragmento"
                    >
                      <Trash2 class="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>

  <!-- Delete Session Dialog -->
  <AlertDialog v-model:open="showDeleteSession">
    <AlertDialogContent class="z-[200]">
      <AlertDialogHeader>
        <AlertDialogTitle>Eliminar sesion</AlertDialogTitle>
        <AlertDialogDescription>
          Se eliminara "{{ sessionToDelete?.name }}" y todos sus fragmentos. Esta accion no se puede deshacer.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Cancelar</AlertDialogCancel>
        <AlertDialogAction variant="destructive" @click="doDeleteSession">Eliminar</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>

  <!-- Delete Fragment Dialog -->
  <AlertDialog v-model:open="showDeleteFragment">
    <AlertDialogContent class="z-[200]">
      <AlertDialogHeader>
        <AlertDialogTitle>Eliminar fragmento</AlertDialogTitle>
        <AlertDialogDescription>
          Se eliminara este fragmento de audio. Esta accion no se puede deshacer.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Cancelar</AlertDialogCancel>
        <AlertDialogAction variant="destructive" @click="doDeleteFragment">Eliminar</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>

  <!-- Delete Analysis Dialog -->
  <AlertDialog v-model:open="showDeleteAnalysis">
    <AlertDialogContent class="z-[200]">
      <AlertDialogHeader>
        <AlertDialogTitle>Eliminar análisis</AlertDialogTitle>
        <AlertDialogDescription>
          Se eliminará el análisis de esta sesión. Podrás volver a generarlo después.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Cancelar</AlertDialogCancel>
        <AlertDialogAction variant="destructive" @click="doDeleteAnalysis">Eliminar</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
